import type { BankFeedAccountType } from '@/domain/bankFeed/schemas';

/**
 * Hierarchical query keys for the bank-feed domain.
 *
 * The factory shape lets consumers invalidate at any level:
 *   - `bankFeedKeys.all`                  — invalidate every bank-feed query
 *   - `bankFeedKeys.connections(companyId)`   — invalidate one company's connections list
 *   - `bankFeedKeys.syncSchedules(companyId)` — invalidate one company's settings
 *
 * Always invalidate via this factory's value, never a hand-typed string literal:
 * typos won't lint-fail but will silently break invalidation.
 */
export const bankFeedKeys = {
  all: ['bankFeed'] as const,
  syncSchedules: (companyId: string) => [...bankFeedKeys.all, 'syncSchedules', companyId] as const,
  providers: () => [...bankFeedKeys.all, 'providers'] as const,
  settingsUserPermissions: (companyId: string) => [...bankFeedKeys.all, 'settingsUserPermissions', companyId] as const,
  connections: (companyId: string) => [...bankFeedKeys.all, 'connections', companyId] as const,
  legacyBankingConnection: (connectionId: string) =>
    [...bankFeedKeys.all, 'legacyBankingConnection', connectionId] as const,
  legacyBankingDiscoveredAccounts: (connectionId: string) =>
    [...bankFeedKeys.all, 'legacyBankingDiscoveredAccounts', connectionId] as const,
  legacyBankingLoginAccounts: (connectionId: string) =>
    [...bankFeedKeys.all, 'legacyBankingLoginAccounts', connectionId] as const,
  legacyBankingLinkableCompanies: (companyId: string) =>
    [...bankFeedKeys.all, 'legacyBankingLinkableCompanies', companyId] as const,
  connectionsLimit: (companyId: string) => [...bankFeedKeys.all, 'connectionsLimit', companyId] as const,
  erpAccounts: (companyId: string) => [...bankFeedKeys.all, 'erpAccounts', companyId] as const,
  transactionsHeaders: (accountType: BankFeedAccountType) =>
    [...bankFeedKeys.all, 'transactionsHeaders', accountType] as const,
  transactionsSummary: () => [...bankFeedKeys.all, 'transactionsSummary'] as const,
  transactionsAutocomplete: (field: string, accountType: BankFeedAccountType, currency: string, accountIds: string[]) =>
    [...bankFeedKeys.all, 'transactionsAutocomplete', field, accountType, currency, accountIds] as const,
};
